import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { XpoShellRoute } from '@xpo-ltl/ngx-ltl-core/shell';
import { User } from '@xpo-ltl/sdk-common';
import { DynamicPricingApiService } from '@xpo-ltl/sdk-dynamicpricing';
import { invoke as _invoke, isEmpty as _isEmpty } from 'lodash';
import { BehaviorSubject, interval, of, Subject } from 'rxjs';
import { catchError, delay, filter, retryWhen, skipWhile, take, tap } from 'rxjs/operators';
import { AppName, AppRouteLabels, AppRoutes } from './enums/app-routes.enum';
import { ConfigManagerProperties } from './enums/config-manager-properties.enum';
import { UserPermission } from './enums/user-permission.enum';
import { ApplicationUtilsService } from './services/application-utils.service';
import { UserPermissionService } from './services/user-permission.service';
import { UserService } from './services/user.service';
import { UserRamRoleSelectionComponent } from './shared/components/user-ram-role-selection/user-ram-role-selection.component';

export interface XpoShellRouteSecure extends XpoShellRoute {
  permission?: UserPermission;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
  title: string;
  build: string;
  userRoutes = new BehaviorSubject<XpoShellRoute[]>([]);
  userRoutes$ = this.userRoutes.asObservable();
  isProduction: boolean = false;
  region: string;
  ramRoles: Array<string> = [];
  destroy$: Subject<any> = new Subject();
  routes: any[] = [
    {
      label: AppRouteLabels.rfp,
      path: `${AppRoutes.RFP}/${AppRoutes.LIST_PAGE}`,
      permission: UserPermission.DptRfpList,
    },
    {
      label: AppRouteLabels.dynamicRules,
      path: `${AppRoutes.DYNAMIC_RULE}/${AppRoutes.LIST_PAGE}`,
      permission: UserPermission.DynamicRuleList,
    },
    {
      label: AppRouteLabels.customers,
      path: AppRoutes.CUSTOMER_EXCLUDE,
      permission: UserPermission.CustomerList,
    },
    {
      label: AppRouteLabels.customerGroups,
      path: AppRoutes.CUSTOMER_GROUP,
      permission: UserPermission.CustomerGroups,
    },
    {
      label: AppRouteLabels.testHarness,
      path: AppRoutes.RATE_QUOTE,
      permission: UserPermission.TestHarness,
    },
    /* {
      label: AppRouteLabels.testHarness,
      path: null,
      children: [
        {
          label: AppRouteLabels.shipmentVisibility,
          path: AppRoutes.SHIPMENT_VISIBILITY,
          permission: UserPermission.ShipmentVisibility,
        },
        {
          label: AppRouteLabels.testHarness,
          path: AppRoutes.RATE_QUOTE,
          permission: UserPermission.TestHarness,
        },
      ],
    },*/
    {
      label: AppRouteLabels.admin,
      path: null,
      children: [
        {
          label: AppRouteLabels.ac,
          path: `${AppRoutes.ACCESSORIAL_CHARGE}/${AppRoutes.LIST_PAGE}`,
          permission: UserPermission.AccessorialCharges,
        },
        {
          label: AppRouteLabels.amc,
          path: `${AppRoutes.ABSOLUTE_MINIMUM_CHARGE}/${AppRoutes.LIST_PAGE}`,
          permission: UserPermission.AbsoluteMinimumCharges,
        },
        {
          label: AppRouteLabels.dptDiscount,
          path: `${AppRoutes.DPT_DISCOUNT}`,
          permission: UserPermission.DptDiscount,
        },
        {
          label: AppRouteLabels.pricingCodes,
          path: AppRoutes.PRICING_CODES,
          permission: UserPermission.PricingCodes,
        },
      ],
    },
    {
      label: AppRouteLabels.dashboard,
      path: AppRoutes.DYNAMIC_PRICING,
      permission: UserPermission.Dashboard,
    },
  ];

  constructor(
    private dpApiService: DynamicPricingApiService,
    private configManagerService: ConfigManagerService,
    private userPermissionService: UserPermissionService,
    private userService: UserService,
    public applicationUtilsService: ApplicationUtilsService,
    private titleService: Title,
    private router: Router,
    private dialog: MatDialog
  ) {
    /** Shell setup */
    /** Shell setup */
    this.region = this.configManagerService.getSetting<string>(ConfigManagerProperties.region);
    this.isProduction = this.configManagerService.getSetting<boolean>(ConfigManagerProperties.production);
    this.title = AppName.dynamic_pricing;
    this.build = configManagerService.getSetting<string>(ConfigManagerProperties.buildVersion);
    this.userPermissionService.validRoutes = this.routes;
    if (!this.isProduction) {
      this.titleService.setTitle(`${this.title} - ${this.region}`);
    }

    this.userPermissionService.userPermissions$
      .pipe(filter((permissions) => permissions !== undefined))
      .subscribe((permissions) => {
        const hasPermissions = permissions && permissions.length > 0;
        if (hasPermissions) {
          const permissionsBasedRoutes = this.getUserRoutes(this.routes, permissions);
          this.userRoutes.next(permissionsBasedRoutes);
          if (this.router.url === AppRoutes.ROOT) {
            if (permissions.includes(UserPermission.CreateDynamicRule)) {
              this.router.navigate([`${AppRoutes.DYNAMIC_RULE}/${AppRoutes.LIST_PAGE}`]);
            } else if (permissions.includes(UserPermission.DynamicRuleList)) {
              this.router.navigate([`${AppRoutes.DYNAMIC_RULE}/${AppRoutes.LIST_PAGE}`]);
            }
          }
        } else {
          this.router.navigate([AppRoutes.NOT_AUTHORIZED_PAGE]);
        }
      });
  }

  ngOnInit() {
    this.handleLoggedInUser();
  }

  private getUserRoutes(routes: XpoShellRouteSecure[], permissions: Array<string>): XpoShellRoute[] {
    return routes
      .filter((route: XpoShellRouteSecure) => {
        if (!route.children) {
          return !route.permission || permissions.includes(route.permission);
        } else {
          const child = route.children.filter(
            (children: any) => !children.permission || permissions.includes(children.permission)
          );
          return !!child.length;
        }
      })
      .map((route: XpoShellRouteSecure) => {
        return {
          label: route.label,
          path: route.path,
          children: route.children
            ? route.children.filter(
                (children: any) => !children.permission || permissions.includes(children.permission)
              )
            : null,
        };
      });
  }

  private handleLoggedInUser(): void {
    this.dpApiService
      .loggedInUser()
      .pipe(
        retryWhen((errors) => errors.pipe(delay(1000), take(5))),
        catchError((error) => {
          console.error(error);
          return of(undefined);
        })
      )
      .subscribe(
        (user: User) => {
          this.userService.User = user;
          this.ramRoles = this.userPermissionService.getRamRoles(user.roles);
          this.userPermissionService.ramRoles.next(this.ramRoles);
          this.userPermissionService.userLoaded$.next(true);
          this.getRoutesBasedOnPermission(false);
          this.setDynatraceUserIdentity(user);
        },
        (error) => {
          console.error(error);
        }
      );
  }

  openRamRolesSwitchDialog() {
    const dialogRef = this.dialog.open(UserRamRoleSelectionComponent, {
      width: '500px',
      height: '500px',
      panelClass: 'switch-ram-roles-dialog',
      data: {
        ramRoles: this.ramRoles,
      },
    });
    dialogRef.afterClosed().subscribe((resp: string[]) => {
      if (Array.isArray(resp)) {
        this.ramRoles = resp;
        this.userPermissionService.ramRoles.next(resp);
        this.getRoutesBasedOnPermission(true);
      }
    });
  }

  releaseNotesOpen(): void {
    window.open(this.applicationUtilsService.releaseNotesLink);
  }

  releaseNotesSelectedAndOpen(): void {
    this.releaseNotesOpen();
  }

  signOut(): void {
    // Removing the local storage keys
    localStorage.clear();
    sessionStorage.clear();
  }

  getRoutesBasedOnPermission(updatedRamRoleSelection: boolean = false) {
    const permissionsBasedRoutes = this.userPermissionService.getUserRoutes(this.routes);
    this.userRoutes.next(permissionsBasedRoutes);
    if (updatedRamRoleSelection) {
      this.userPermissionService.routeUserBasedOnPermission(permissionsBasedRoutes);
    }
  }

  isChildRouteActive(children: any[]): boolean {
    return children?.some((child) => child.path && this.router.isActive(child.path, false));
  }

  private setDynatraceUserIdentity(user: User): void {
    const setUser = (): void =>
      _invoke(
        window['dtrum'],
        'identifyUser',
        !_isEmpty(user.emailAddress) ? user.emailAddress : !_isEmpty(user.userId) ? user.userId : 'PND_USER'
      );

    if ((window['dtrum'] || {}).identifyUser) {
      setUser();
    } else {
      let retryCount: number = 0;
      interval(1000)
        .pipe(
          tap(() => retryCount++),
          skipWhile(() => !(window['dtrum'] || {}).identifyUser && retryCount <= 60),
          take(1)
        )
        .subscribe(() => {
          setUser();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
